import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudEditorParentService, CrudEditorSelfService, CrudItemComponent, ROLE_METADATA, ROLE_TYPE, Role, RoleService, USER_TYPE, User, UserService, UserStorage, onValueChangeEvent, requiredValidator } from '@scisys-uim/uim-common';
import { equiv } from '@thi.ng/equiv';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AssetsUserDetailsService, AssetsUserStorage } from 'assets-service';

const usernamePattern: RegExp = new RegExp("^[a-zA-Z][a-zA-Z0-9_\.\@]*$");

@Component({
  selector: 'app-user-item',
  templateUrl: './user-item.component.html',
  styleUrls: ['./user-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class UserItemComponent extends CrudItemComponent<User, string> {

  protected systemConfig: any;

  public form: FormGroup;

  allRoles: Role[];
  @ViewChild('roles') rolesInput: ElementRef;
  private rolesSelectionListSubject: Subject<Role[]> = new BehaviorSubject([]);
  rolesSelectionList: Observable<Role[]> = this.rolesSelectionListSubject.asObservable();


  constructor(protected userService: UserService,
    protected roleService: RoleService,
    protected assetsUserDetailsService: AssetsUserDetailsService,
    protected assetsUserStorage: AssetsUserStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(USER_TYPE, fb, elementRef, router, route, userStorage, dialog, userService, injector, snackbar);
  }

  createForm(): FormGroup {
    return this.fb.group({
      username: [null, [requiredValidator, (control) => {
        return (usernamePattern.test(control.value)) ? null : { roleNameFormat: "Must contain only alphanumeric characters and these special characters: '@ _ .'" }
      }]],
      enabled: [null],
      passwordLoginEnabled: [null],
      systemProvided: [null],
      roles: [null]
    });
  }


  ngAfterViewInit(): void {
    if (this.roles) {
      onValueChangeEvent(this.roles, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.roles = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.rolesSelectionListFilter(value);
        }
      });
    }
  }

  getChangedSingleItemEquiv(): (a, b) => boolean {
    return (a, b) => {
      if (a.name && b.name) {
        return a.name == b.name;
      }
      return equiv(a, b);
    };
  }

  ngOnInit() {
    super.ngOnInit();

    function initOptions(config: any): any {
      config = config || {};
      return config;
    }
    this.systemConfig = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.systemConfig = initOptions(config);
    });

    this.rolesLoadSelectionList();
  }

  isCurrentUser(item: User): boolean {
    if (!item) {
      return false;
    }
    const currentUser = this.userStorage.get();
    if (currentUser) {
      return currentUser.username == item.username;
    }
    return false;
  }

  itemSet() {
    super.itemSet();
    if (this.isCurrentUser(this.item)) {
      this.config.readOnly = true;
    }
    this.rolesSelectionListFilter(null);
  }

  setDefaults() {
    if (!this.readOnly && this.createMode) {
      this.item.systemProvided = false;

      this.assetsUserStorage.nextNonNullItem().subscribe(userDetails => {
        // re-trigger updating drop-downs.
        this.itemSet();
      });
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    this.systemProvided.disable();
    this.passwordLoginEnabled.disable();
    if (!this.createMode) {
      this.username.disable();
    }
    if (this.item && this.item.systemProvided) {
      this.enabled.disable();
    }
  }

  allowsEditItem() {
    if (super.allowsEditItem()) {
      return this.item && !this.item.systemProvided;
    }
    return false;
  }

  systemProvidedChanging(value: boolean, unsetDependents: boolean = true) { }

  systemProvidedChanged(value: boolean, unsetDependents: boolean = true) { }

  enabledChanging(value: boolean, unsetDependents: boolean = true) { }

  enabledChanged(value: boolean, unsetDependents: boolean = true) { }

  rolesChanging(value: Role[], unsetDependents: boolean = true) { }

  rolesChanged(value: Role[], unsetDependents: boolean = true) { }

  rolesDisplay(item: Role) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ROLE_TYPE.description(item);
  }

  selectedRole(event: MatAutocompleteSelectedEvent): void {
    var newItem: Role = this.allRoles.find(p => p.name === event.option.viewValue);
    if (this.item.roles) {
      this.item.roles.push(newItem);
    } else {
      this.item.roles = [newItem];
    }
    this.rolesInput.nativeElement.value = '';
    this.roles.setValue(null);
    this.rolesSelectionListFilter(null);
  }

  rolesLoadSelectionList() {
    this.roleService.findAll().subscribe(
      data => {
        var roles = data.items;
        if (!this.userStorage.currentUserHasSuperuserAuthority()) {
          // TODO - make work - ownPrivileges/allPrivileges for SUPERUSER role should have them all.
          //const superuserAuthority = this.userStorage.superuserAuthority;
          //const superUserRoles = roles.filter(r => r.allPrivileges.map(p => p.name).find(p => p == superuserAuthority) != null);
          const superUserRoleNames = ['SUPERUSER'];
          roles = roles.filter(r => !superUserRoleNames.includes(r.name));
        }
        this.allRoles = roles;
        this.rolesSelectionListFilter(null);
      }
    );
  }

  rolesSelectionListFilter(text: string): void {
    var filtered = this.allRoles ? this.allRoles.filter(p => !(this.item.roles || []).find(o => o.name == p.name)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(text.toLowerCase()));
    }
    this.rolesSelectionListSubject.next(filtered);
  }

  rolesCompare(a: any, b: any) {
    return ROLE_METADATA.equals(a, b);
  }

  removeRole(role: Role): void {
    const index = this.item.roles.indexOf(role);

    if (index >= 0) {
      this.item.roles.splice(index, 1);
      this.rolesSelectionListFilter(null);
    }
  }

  usernameChanging(value: string, unsetDependents: boolean = true) { }

  usernameChanged(value: string, unsetDependents: boolean = true) { }


  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get username() {
    return this.control('username');
  }

  get systemProvided() {
    return this.control('systemProvided');
  }

  get passwordLoginEnabled() {
    return this.control('passwordLoginEnabled');
  }

  get enabled() {
    return this.control('enabled');
  }

  get roles() {
    return this.control('roles');
  }
}





